import { useEffect, useState } from 'react';
import {
  Autocomplete,
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  OnSelectChange,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import { ExpectedOriginFundTypes } from './types';
import RegulatedCompanyRow from './row';
import { ExpectedFundsOriginsFormValues } from '../../../../../hoc/new-application/types';
import { CustomerStatus } from '../../../../../hoc/common/types';

import cn from './styles.module.css';

const ExpectedOriginFund = ({
  expectedFundsOrigins,
  setExpectedFundsOriginsValues,
  expectedFundsOrigin,
  index,
  status,
  onDeleteExpectedFundsOrigins,
  deleteExpectedFundsOriginsInProgress,
  selectedExpectedFundsOriginsId,
  currencies,
  countries,
}: ExpectedOriginFundTypes) => {
  const [formValues, setFormValues] =
    useState<ExpectedFundsOriginsFormValues>();
  const [expanded, setExpanded] = useState(false);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextValues = expectedFundsOrigins?.map((item) => {
      const result = { ...item };
      const nextFormValues = { ...formValues };

      if (nextFormValues.id === item.id) {
        if (e.target.name === `currencyId-${item.id}`) {
          nextFormValues.currencyId = e.target.value as string;
          result.currencyId = e.target.value as string;
        } else if (
          e.target.name === `mainCounterpartiesAndWebsites-${item.id}`
        ) {
          nextFormValues.mainCounterpartiesAndWebsites = e.target
            .value as string;
          result.mainCounterpartiesAndWebsites = e.target.value as string;
        } else if (e.target.name === `countryOfIncorporationId-${item.id}`) {
          nextFormValues.countryOfIncorporationId = e.target.value as string;
          result.countryOfIncorporationId = e.target.value as string;
        } else if (e.target.name === `counterpartyBankCountryIds-${item.id}`) {
          nextFormValues.counterpartyBankCountryIds = e.target
            .value as string[];
          result.counterpartyBankCountryIds = e.target.value as string[];
        } else if (e.target.name === `activityCountryIds-${item.id}`) {
          nextFormValues.activityCountryIds = e.target.value as string[];
          result.activityCountryIds = e.target.value as string[];
        } else if (e.target.name === `mainActivities-${item.id}`) {
          nextFormValues.mainActivities = e.target.value as string;
          result.mainActivities = e.target.value as string;
        } else if (e.target.name === `transactionReason-${item.id}`) {
          nextFormValues.transactionReason = e.target.value as string;
          result.transactionReason = e.target.value as string;
        }

        setFormValues(nextFormValues as ExpectedFundsOriginsFormValues);
      }

      return result;
    });

    setExpectedFundsOriginsValues(
      nextValues as ExpectedFundsOriginsFormValues[],
    );
  };

  useEffect(() => {
    if (expectedFundsOrigin) {
      setFormValues(expectedFundsOrigin);

      if (expectedFundsOrigin?.id?.includes('expected-funds-origins')) {
        setExpanded(true);
      }
    }
  }, [expectedFundsOrigin]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${cn.expectedFundsOriginsWrapperContent} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Counterparties #${index + 1}`}
          </button>
          {status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.deleteAccountCurrencyButton}
              onClick={() =>
                onDeleteExpectedFundsOrigins(expectedFundsOrigin?.id as string)
              }
            >
              {deleteExpectedFundsOriginsInProgress &&
              selectedExpectedFundsOriginsId === expectedFundsOrigin?.id ? (
                <IconLoader width={18} height={18} />
              ) : (
                <IconTrash />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <>
          <RegulatedCompanyRow title="Account currency" annualIncome>
            <Select
              name={`currencyId-${expectedFundsOrigin?.id}`}
              placeholder="Select currency..."
              list={currencies}
              value={formValues?.currencyId}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Names of main counterparties + website"
            annualIncome
            textarea
          >
            <Textarea
              name={`mainCounterpartiesAndWebsites-${expectedFundsOrigin?.id}`}
              placeholder="Describe the names..."
              value={formValues?.mainCounterpartiesAndWebsites}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Country of incorporation" annualIncome>
            <Autocomplete
              name={`countryOfIncorporationId-${expectedFundsOrigin?.id}`}
              placeholder="Select country..."
              list={countries}
              value={formValues?.countryOfIncorporationId}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: `countryOfIncorporationId-${expectedFundsOrigin?.id}`,
                  },
                });
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Countries of counterparty's banks"
            annualIncome
          >
            <Select
              name={`counterpartyBankCountryIds-${expectedFundsOrigin?.id}`}
              placeholder="Select countries..."
              list={countries}
              value={formValues?.counterpartyBankCountryIds}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              mode={SelectType.MULTIPLE}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Countries of activities" annualIncome>
            <Select
              name={`activityCountryIds-${expectedFundsOrigin?.id}`}
              placeholder="Select countries..."
              list={countries}
              value={formValues?.activityCountryIds}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              mode={SelectType.MULTIPLE}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Main activities" annualIncome textarea>
            <Textarea
              name={`mainActivities-${expectedFundsOrigin?.id}`}
              placeholder="Enter description..."
              value={formValues?.mainActivities}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow
            title="Reason for transaction with the Customer"
            annualIncome
            textarea
          >
            <Textarea
              name={`transactionReason-${expectedFundsOrigin?.id}`}
              placeholder="Enter description..."
              value={formValues?.transactionReason}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
            />
          </RegulatedCompanyRow>
        </>
      )}
    </div>
  );
};

export default ExpectedOriginFund;
